import { useMutation, useQuery } from "@apollo/client";
import { Button, ButtonGroup, Card } from "@blueprintjs/core";
import { useState } from "react";
import {
  CreateDistributorInput,
  UpdateDistributorInput,
  Distributor,
} from "../../api/schema";
import {
  FlexContainer,
  FlexGrow,
  FlexRowStretch,
} from "../../components/flex/Flex";
import { DeleteModal } from "../../components/form/DeleteModal";
import { ModalInputType } from "../../components/form/Form";
import {
  InputFormMode,
  UpdateStateHelper,
  ValuesVariables,
} from "../../components/form/InputForm";
import { InputModal } from "../../components/form/InputModal";
import { ListView } from "../../components/query/ListView";
import { Spinner } from "../../components/Spinner";
import { UserLanguage } from "../../lang/lang";
import {
  CreateDistributorMutation,
  CreateDistributorResponse,
  DeleteDistributorMutation,
  DeleteDistributorResponse,
  DistributorListQuery,
  DistributorListResponse,
  UpdateDistributorMutation,
  UpdateDistributorResponse,
} from "./graphql";

export const DistributorListView = (props: any) => {
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [deleteModalState, setDeleteModalState] = useState<{
    open: boolean;
    id?: number;
    prettyPrint?: string;
  }>({ open: false, id: undefined, prettyPrint: undefined });

  const [createDistributor] = useMutation<
    CreateDistributorResponse,
    ValuesVariables<CreateDistributorInput>
  >(CreateDistributorMutation);
  const [updateDistributor] = useMutation<
    UpdateDistributorResponse,
    ValuesVariables<UpdateDistributorInput>
  >(UpdateDistributorMutation);
  const [deleteDistributor] = useMutation<DeleteDistributorResponse>(
    DeleteDistributorMutation
  );
  const [updateDistributorState, setUpdateDistributorState] = useState<
    UpdateStateHelper<UpdateDistributorInput>
  >({ open: false, item: undefined });

  const listQuery = useQuery<DistributorListResponse>(DistributorListQuery, {
    fetchPolicy: "no-cache",
  });

  console.log("redraw", listQuery.data);
  console.log(updateDistributorState);

  return (
    <FlexContainer>
      <Card elevation={3}>
        <FlexRowStretch>
          <div>
            <h2>{UserLanguage().pages.distributor.list}</h2>
          </div>
          <FlexGrow />
          <div>
            <Button
              intent="success"
              icon="plus"
              onClick={() => setCreateModalOpen(true)}
            >
              {UserLanguage().actions.create}
            </Button>
          </div>
        </FlexRowStretch>
        <InputModal<
          CreateDistributorInput,
          CreateDistributorResponse,
          CreateDistributorInput
        >
          keys={{
            name: {
              type: ModalInputType.String,
              icon: "id-number",
              placeholder: UserLanguage().data.name,
            },
            short_name: {
              type: ModalInputType.String,
              placeholder: UserLanguage().data.distributor.short_name,
            },
            search_url: {
              type: ModalInputType.String,
              placeholder: UserLanguage().data.distributor.search_url,
            },
          }}
          open={createModalOpen}
          texts={UserLanguage().pages.distributor.actions}
          onClose={() => setCreateModalOpen(false)}
          mutation={createDistributor}
          onSuccess={() => listQuery.refetch()}
          resetOnClose
          mode={InputFormMode.Create}
        />
        <InputModal<
          UpdateDistributorInput,
          UpdateDistributorResponse,
          UpdateDistributorInput
        >
          keys={{
            id: {
              type: ModalInputType.String,
              readonly: true,
              label: UserLanguage().data.id,
            },
            name: {
              type: ModalInputType.String,
              icon: "id-number",
              placeholder: UserLanguage().data.name,
            },
            short_name: {
              type: ModalInputType.String,
              placeholder: UserLanguage().data.distributor.short_name,
            },
            search_url: {
              type: ModalInputType.String,
              placeholder: UserLanguage().data.distributor.search_url,
            },
          }}
          open={updateDistributorState.open}
          texts={UserLanguage().pages.distributor.actions}
          input={updateDistributorState.item}
          onClose={() =>
            setUpdateDistributorState({ open: false, item: undefined })
          }
          mutation={updateDistributor}
          onSuccess={() => {
            listQuery.refetch();
            setUpdateDistributorState({ open: false, item: undefined });
          }}
          resetOnClose
          mode={InputFormMode.Edit}
        />
        <DeleteModal<DeleteDistributorResponse>
          id={deleteModalState.id}
          open={deleteModalState.open}
          mutation={(id) => deleteDistributor({ variables: { id } })}
          texts={UserLanguage().pages.distributor.actions}
          resetOnClose
          onClose={() => {
            setDeleteModalState({ open: false, id: undefined });
          }}
          onSuccess={() => {
            setDeleteModalState({ open: false, id: undefined });
            listQuery.refetch();
          }}
          type={UserLanguage().data.distributor.label}
          prettyPrint={deleteModalState.prettyPrint}
        />
        <ListView<DistributorListResponse, Distributor>
          cols={{
            id: { label: UserLanguage().data.id },
            name: { label: UserLanguage().data.name },
            short_name: { label: UserLanguage().data.distributor.short_name },
            search_url: { label: UserLanguage().data.distributor.search_url },
            actions: {
              label: UserLanguage().actions.label,
              render: (item) => (
                <ButtonGroup>
                  <Button
                    intent="primary"
                    rightIcon="edit"
                    onClick={() =>
                      setUpdateDistributorState({
                        open: true,
                        item: item as any as UpdateDistributorInput, // TODO: fix me! stupid hack xd
                      })
                    }
                  >
                    {UserLanguage().actions.edit}
                  </Button>
                  <Button
                    intent="danger"
                    rightIcon="delete"
                    onClick={() =>
                      setDeleteModalState({
                        open: true,
                        id: item.id,
                        prettyPrint: `${UserLanguage().data.name}: ${
                          item.name
                        }`,
                      })
                    }
                  >
                    {UserLanguage().actions.delete}
                  </Button>
                </ButtonGroup>
              ),
            },
          }}
          error={<p>Error</p>}
          loading={<Spinner />}
          itemKey="distributors"
          query={listQuery}
        />
      </Card>
    </FlexContainer>
  );
};
